// src/utils/capturedFields.ts — what a voice agent captured about a caller,
// read off the contact record.
//
// A voice agent collects far more than the contact form models: wellness goal,
// expected check-in month, preferred callback window, companion, sentiment,
// lead quality. cpass maps name/phone/email/source/notes onto columns and puts
// EVERYTHING ELSE into contacts.custom_fields as JSON. For the live Atmantan
// agent that is 13 values per lead, none of which any screen has ever shown.
//
// Three contract details make this easy to get wrong, and all three are real:
//
//  - the API sends `customFields` (camelCase, Jackson off ContactDTO); the
//    Contact type declared `custom_fields` (snake_case)
//  - the value is a JSON **string**, not an object — ContactDTO.customFields is
//    a `String` off a LONGTEXT column, so `contact.customFields.first_name` is
//    undefined even when you have spelled the key right
//  - contacts.service.transformContact is a whitelist, and it dropped the field
//    entirely, so a component could not have read it under either spelling
//
// This module therefore reads either spelling and either type, and treats
// anything it cannot parse as "no captured fields" rather than throwing: a
// contact with a malformed blob must still open.
//
// The key set belongs to the customer, not to us. Nothing here enumerates,
// prioritises or filters by business-specific field names — a salon's agent
// will capture an entirely different vocabulary and must render just as well
// as the wellness retreat this was built against.

export type CapturedField = { key: string; label: string; value: string };

/**
 * The captured blob as an object, whatever shape it arrived in.
 *
 * Returns `{}` — never throws, never null — for every unusable input, so
 * callers can treat "no captured data" and "unreadable captured data" the same
 * way, which is what the UI wants: show nothing extra.
 */
export function parseCapturedFields(contact: any): Record<string, any> {
  if (!contact) return {};
  const raw = contact.customFields ?? contact.custom_fields;
  if (raw === null || raw === undefined) return {};

  // Already an object (a future contract change, or a caller that pre-parsed).
  // Arrays are rejected: a list has no field names to show.
  if (typeof raw === 'object') {
    return Array.isArray(raw) ? {} : (raw as Record<string, any>);
  }
  if (typeof raw !== 'string') return {};

  const trimmed = raw.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    // `"hello"` and `42` are valid JSON but carry no fields, and `[...]` has no
    // names — only a plain object is renderable as label/value rows.
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, any>)
      : {};
  } catch {
    return {};
  }
}

/**
 * A snake_case machine key, rendered for a person.
 *
 * Underscores become spaces and words are capitalised; the key itself is never
 * translated through a lookup table, because the keys are the customer's and a
 * table would only ever cover the ones we happened to have seen. `ai_sentiment`
 * is special-cased only as an initialism, which is a property of English, not
 * of any one business's schema.
 */
export function labelFor(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bAi\b/g, 'AI')
    .replace(/\bId\b/g, 'ID');
}

/**
 * One captured value as display text.
 *
 * Objects and arrays are stringified as JSON rather than interpolated, because
 * `String({a:1})` is the string "[object Object]" — which tells the person
 * reading it nothing and looks like a bug. Nested structure is rare in captured
 * data but it is free-form JSON, so it is possible, and showing the real
 * content is better than showing a placeholder for it.
 */
export function formatCapturedValue(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      // Circular structures cannot come off JSON.parse, but this function is
      // exported and a caller may hand it anything.
      return '';
    }
  }
  return String(value);
}

/**
 * The captured fields to render, ready for display.
 *
 * Blank values are dropped. The agent sends every key in its tool schema on
 * every call, filling the ones the caller never answered with "" — on the real
 * Atmantan lead 4 of 15 keys arrived empty. Rendering them would pad the panel
 * with empty rows and bury the answers that were actually given. A field that
 * is missing and a field that is present-but-empty mean the same thing here:
 * the agent did not learn it.
 *
 * Order is alphabetical by key. The stored order is not meaningful — it is
 * whatever order the backend's map serialised in — so there is nothing to
 * preserve, and alphabetical is stable, predictable across contacts, and does
 * not depend on knowing what any particular business calls its fields.
 */
export function capturedFieldList(contact: any): CapturedField[] {
  const parsed = parseCapturedFields(contact);
  return Object.keys(parsed)
    .map((key) => ({ key, label: labelFor(key), value: formatCapturedValue(parsed[key]) }))
    .filter((f) => f.value !== '' && f.key.trim() !== '')
    .sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * A display name for a contact whose own `name` is missing, or is just its
 * phone number repeated back.
 *
 * This is not cosmetic. The agent tool sends first_name/last_name while cpass
 * mapped only `name`, so live contacts exist whose name is literally
 * "+919024685946" while "Gaurav" and "Mehta" sit in the captured blob. The
 * backend composes the name for new leads now, but no deploy rewrites the rows
 * already stored, so the list keeps showing phone numbers as names until this
 * reads the captured halves.
 *
 * A contact's own name always wins when it is a real name — this only fills a
 * gap, it never overrides what someone typed.
 */
export function displayName(contact: any): string {
  const own = String(contact?.name ?? '').trim();
  const phones: string[] = (contact?.phoneNumbers ?? contact?.phone_numbers ?? [])
    .map((p: any) => String(p?.phoneNumber ?? p?.phone_number ?? p?.number ?? '').trim())
    .filter(Boolean);

  // "Looks like a phone number" is deliberately narrow: digits and phone
  // punctuation only, at least 7 of them. A person's name does not match it.
  const looksLikeAPhone =
    own !== '' && (phones.includes(own) || /^\+?[\d\s()-]{7,}$/.test(own));
  if (own && !looksLikeAPhone) return own;

  const f = parseCapturedFields(contact);
  const joined = [f.first_name, f.last_name]
    .map((v) => formatCapturedValue(v))
    .filter(Boolean)
    .join(' ')
    .trim();
  return joined || own;
}
